import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  clearCheckoutSession,
  readCheckoutSession,
  runRazorpayCheckout,
} from '@/lib/billing/razorpayCheckout';

export function BillingCheckoutPage() {
  const token = localStorage.getItem('storycraft_token');
  if (!token) {
    return <Navigate to="/login" replace state={{ from: '/pricing' }} />;
  }
  return <BillingCheckoutView />;
}

function BillingCheckoutView() {
  const startedRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Opening Razorpay checkout…');
  const [creditsGranted, setCreditsGranted] = useState(null);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const session = readCheckoutSession();
    if (!session?.order_id || !session?.key_id) {
      setStatus('error');
      setMessage('Checkout session expired. Close this tab and try again from the pricing page.');
      return;
    }

    runRazorpayCheckout(session)
      .then((result) => {
        if (result?.ok) {
          setStatus('success');
          setCreditsGranted(result.verified?.credits_granted ?? session.credits);
          const planName = result.verified?.current_plan_name;
          setMessage(
            planName
              ? `Payment successful. Current plan: ${planName}. Credits are in your wallet.`
              : 'Payment successful. Credits have been added to your wallet.',
          );
          return;
        }
        if (result?.cancelled) {
          setStatus('cancelled');
          setMessage('Payment cancelled. You can close this tab and try again.');
          return;
        }
        setStatus('error');
        setMessage('Payment could not be completed. Please try again.');
      })
      .catch(() => {
        setStatus('error');
        setMessage('Could not load Razorpay checkout.');
      });
  }, []);

  return (
    <div
      className="billing-checkout-page min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
      data-testid="billing-checkout-page"
    >
      <div className="pricing-panel max-w-md w-full rounded-3xl p-8 text-center space-y-4">
        {status === 'loading' ? (
          <Loader2 className="w-10 h-10 mx-auto animate-spin" style={{ color: 'hsl(var(--primary))' }} />
        ) : null}
        {status === 'success' ? (
          <CheckCircle className="w-10 h-10 mx-auto text-emerald-500" />
        ) : null}
        {status === 'error' || status === 'cancelled' ? (
          <XCircle className="w-10 h-10 mx-auto text-amber-500" />
        ) : null}

        <h1 className="pricing-heading text-xl font-bold">Twinnee checkout</h1>
        <p className="pricing-muted text-sm leading-relaxed">{message}</p>
        {status === 'success' && creditsGranted != null ? (
          <p className="text-sm font-semibold" style={{ color: 'hsl(var(--primary))' }}>
            +{Number(creditsGranted).toLocaleString('en-IN')} credits
          </p>
        ) : null}

        <div className="flex flex-col gap-2 pt-2">
          {status === 'success' ? (
            <Button
              type="button"
              className="rounded-full btn-primary-theme"
              onClick={() => {
                clearCheckoutSession();
                window.close();
              }}
            >
              Close tab
            </Button>
          ) : null}
          {status === 'error' || status === 'cancelled' ? (
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => window.close()}
            >
              Close tab
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
